import {
  AccordionGroup,
  Box,
  HStack,
  MixpanelEvent,
  Stack,
  Tab,
  TabList,
  TabPanels,
  TabSize,
  Tabs,
  Text,
  tabLabelerEnumerated,
  useMixpanel,
  usePersistedTabs,
  usePortal,
  useTabs,
  type PersistedDefaultTabProps,
  type TabProps,
} from '@talos/kyoko';
import { useCallback } from 'react';
import { useBlotterTableTabCloning } from '../../../../providers/AppConfigRedux/useBlotterTableTabCloning';
import { CareOrderActionButtons } from '../CareOrderActionButtons';
import { CareOrderManagementBlotter, type CareOrderManagementTabData } from './CareOrderManagementBlotter';

const CARE_ORDER_MANAGEMENT_BLOTTER_PREFIX = 'care-order-management-blotter';
const CARE_ORDER_MANAGEMENT_BLOTTER_PORTAL_ID = 'care-order-management-blotter-portal';
const ALL_CARE_ORDER_MANAGEMENT_TAB_ID = 'all-care-order-management';
const tabLabeler = tabLabelerEnumerated('Care Order Management');

interface CareOrderManagementWrapperProps {
  panelId: string;
}

interface CareOrderManagementTabProps extends TabProps, CareOrderManagementTabData {}

const defaultTabs: PersistedDefaultTabProps<CareOrderManagementTabProps>[] = [
  {
    id: ALL_CARE_ORDER_MANAGEMENT_TAB_ID,
    label: 'All',
    editable: true,
    closable: false,
  },
];
const excludedIDs = defaultTabs.map(t => t.id);

export const CareOrderManagementWrapper = ({ panelId }: CareOrderManagementWrapperProps) => {
  const mixpanel = useMixpanel();
  const { setPortalRef: filtersContainerRef } = usePortal(CARE_ORDER_MANAGEMENT_BLOTTER_PORTAL_ID);
  const getBlotterID = useCallback(
    (tabID: string) => `${CARE_ORDER_MANAGEMENT_BLOTTER_PREFIX}/${panelId}/${tabID}`,
    [panelId]
  );

  const handleAdd = useCallback(() => {
    mixpanel.track(MixpanelEvent.AddTab);
  }, [mixpanel]);

  const persistedTabs = usePersistedTabs<CareOrderManagementTabProps>(
    `${CARE_ORDER_MANAGEMENT_BLOTTER_PREFIX}/${panelId}`,
    {
      defaultInitialItems: defaultTabs,
      defaultInitialSelectedIndex: 0,
      excludedIDs,
    }
  );

  const tabs = useTabs<CareOrderManagementTabProps>({
    ...persistedTabs,
    showAddTab: true,
    tabLabeler,
    onAdd: handleAdd,
    onClone: useBlotterTableTabCloning({ getBlotterID }),
  });
  const handleCloneTab = useCallback(() => {
    tabs.clone(tabs.selectedIndex);
  }, [tabs]);

  return (
    <Stack h="100%">
      <HStack
        justifyContent="space-between"
        px="spacing12"
        minHeight="40px"
        borderBottom="1px solid var(--backgroundBody)"
        background="backgroundContent"
      >
        <Text color="colorTextImportant" fontWeight={500}>
          Care Order Management
        </Text>
        <CareOrderActionButtons />
      </HStack>
      <Tabs {...tabs} h="100%" size={TabSize.Large}>
        <TabList rightItems={<Box ref={filtersContainerRef} />} background="backgroundContent" minHeight="40px">
          {tabs.items.map((tab, idx) => (
            <Tab key={idx} {...tab} reorderable={true} />
          ))}
        </TabList>
        <TabPanels flex={1} display="flex" flexDirection="column">
          {tabs.items.map(tab => (
            <AccordionGroup key={tab.id}>
              <CareOrderManagementBlotter
                blotterID={`${CARE_ORDER_MANAGEMENT_BLOTTER_PREFIX}/${panelId}/${tab.id}`}
                portalId={CARE_ORDER_MANAGEMENT_BLOTTER_PORTAL_ID}
                showHUD={true}
                includeOpenCareOrdersOutsideDateRange={tab.includeOpenCareOrdersOutsideDateRange ?? true}
                onUpdateTabData={updates => {
                  tabs.updateItem({ ...tab, ...updates });
                }}
                onCloneTab={handleCloneTab}
              />
            </AccordionGroup>
          ))}
        </TabPanels>
      </Tabs>
    </Stack>
  );
};
